// @ts-nocheck — Phase-14 R2 bulk JSX→TSX rename.
// Phase-16 R2 deliberately leaves @ts-nocheck in place: the file is 530+
// lines of inline JSX and the type sweep is scheduled for R16 (cleanup)
// after the new sections + primitives land. R2 only adds the auth-aware
// redirect + outcome-first headline.
// =====================================================
// Home / Landing page  —  R-Landing-v2 (D47, 2026-05-26)
// =====================================================
// Wrapped in .home-shell-v2 so the design's white+navy palette,
// typography (Vazirmatn + Plus Jakarta), section layouts, and
// reveal-on-scroll animations apply ONLY on / (and never on /login,
// /dashboard, etc.). Per Q1.a: AGENT ARCHITECTURE preserved with
// new palette. Per Q2.b: topbar wrapped INSIDE this scope (sticky
// inside the wrapper, so it physically cannot render on other routes
// since the wrapper isn't in DOM there). S-REBRAND-EMI موج ۳: چیپ‌های
// کوبرندِ هیرو حذف شدند (تصمیمِ مالک) و همهٔ نام/نشان‌ها از
// src/brand/brand.config.ts می‌آیند.
//
// SW dispose (Commit A): VitePWA disabled + main.tsx top-level
// unregister/clear. This page deploys without any service worker
// caching it = no R-Landing-v1-style precache stickiness.
// =====================================================
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Icon } from "../icons";
import { Stagger, useMouseParallax } from "../motion";
import { Footer, toFa, KnowledgeGraph, ArchStack } from "../shared";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useAuth } from "../auth/AuthContext";
import { formatNumberFa } from "../i18n/format";
import { useLocale } from "../i18n/LocaleProvider";
import { useMediaQuery } from "../hooks/useMediaQuery";
import type { Go } from "../router";
import { Button } from "../ui";
import { useLandingHead } from "./use-landing-head";
// Scoped CSS — every selector is .home-shell-v2-prefixed, so loading
// this file has NO global effect. The CSS only fires inside the
// wrapper div rendered below.
import "./home-v2.css";
// D48 round-2 polish: manual overrides + faculty + testimonials styles
// not covered by the auto-generated home-v2.css.
import "./home-v2-overrides.css";
// S-LP (docs/review/landingpage.md) — the landing redesign sections. All
// branding/stats/copy come from landing/branding.ts (white-label §D.5);
// styles in landing-lp.css are .home-shell-v2-scoped, token-only.
//
// BUNDLE DISCIPLINE: Home is an EAGER route (public LCP path), so every
// static import here lands in the MAIN bundle. The landing sections are all
// below the fold → they load as LAZY chunks (the +72 KiB main-bundle FAIL on
// the first S-LP gate was exactly these static imports). Only the tiny
// branding config stays eager (the hero reads it).
import "./landing-lp.css";
// S-LAND-POLISH — زبانِ بصریِ هم‌راستا با سایتِ رسمیِ گروه. عمداً آخرین
// import است: هم‌ردهٔ specificity با home-v2.css/landing-lp.css می‌ماند و
// فقط به‌خاطرِ ترتیبِ cascade برنده می‌شود ⇒ هیچ !important لازم نیست.
import "./landing-emi.css";
import { BRANDING } from "../landing/branding";
import { BRAND, brandNameForLocale } from "../brand/brand.config";

const AiDemoSection = React.lazy(() => import("../landing/AiDemoSection"));
const PricingSection = React.lazy(() => import("../landing/PricingSection"));
const lz = <K extends string>(name: K) =>
  React.lazy(() =>
    import("../landing/LandingSections").then((m) => ({
      default: m[name as keyof typeof m] as React.ComponentType<any>,
    })),
  );
const TrustBar = lz("TrustBar");
const ProblemSolutionSection = lz("ProblemSolutionSection");
const PlatformArchitectureSection = lz("PlatformArchitectureSection");
const JourneySection = lz("JourneySection");
const ProgramsExplorerSection = lz("ProgramsExplorerSection");
const LiveClassSection = lz("LiveClassSection");
const CredentialVerifySection = lz("CredentialVerifySection");
const MarketplaceSection = lz("MarketplaceSection");
const FeaturesSection = lz("FeaturesSection");
const FaqSection = lz("FaqSection");
const FinalCtaSection = lz("FinalCtaSection");
const StickyMobileCta = lz("StickyMobileCta");

/** Below-the-fold lazy boundary — no fallback flash, sections pop in. */
const L: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense fallback={null}>{children}</React.Suspense>
);
// Plus Jakarta Sans deferred — adding it would have required a
// package-lock.json regen + full npm install in the build container.
// The Latin display text in Home falls back to Vazirmatn + system-ui
// sans-serif (declared in home-v2-overrides.css .home-shell-v2 font-family).
// Visually 95% indistinguishable for the demo; can be re-added in a
// future polish with proper lockfile sync.

interface HomePageProps {
  go: Go;
}

export const HomePage = ({ go }: HomePageProps) => {
  // S-LAND-POLISH م۴ — بازخوردِ صریحِ مالک: لندینگ باید برای کاربرِ
  // لاگین‌کرده هم دیده شود. ریدایرکتِ Phase-16 R2 («/» → dashboard) و
  // اسکلتِ نگه‌دارنده‌اش حذف شدند. نگرانیِ اصلیِ آن تصمیم (نشت‌نکردنِ
  // چرومِ هویت‌دار روی صفحهٔ عمومی) همچنان برقرار است: ناوِ سراسریِ
  // AppShell روی این مسیر پنهان است و ناوِ خودِ لندینگ فقط برچسبِ
  // «داشبورد من» را نشان می‌دهد، نه نام/آواتار/نقشِ کاربر.
  // S-I18N-branding — "landing.brand" (per-deployment override, defaults to
  // {}) is checked first; i18next falls through to "landing"'s defaults for
  // any key it doesn't carry (apps/web/src/landing/branding.ts, docs/BRAND_RULES.md).
  // nsMode:"fallback" is required — without it react-i18next's useTranslation
  // binds `t` to ONLY namespaces[0] and never checks the rest of the array.
  const { t } = useTranslation(["landing.brand", "landing"], { nsMode: "fallback" });
  const { locale } = useLocale();
  // S-FIX-BRAND-1 (R2-071) — نامِ برند از `brand.config.ts` تزریق می‌شود؛
  // پیش‌تر داخلِ خودِ رشتهٔ کاتالوگ هاردکد بود و ری‌برند جا می‌ماندش.
  const brandNameFull = brandNameForLocale(locale);
  useLandingHead(t("home.head.title"), t("home.head.description", { brand: brandNameFull }), "/");
  const auth = useAuth();
  const isAuthed = auth.isAuthenticated;
  /** م۳/م۴ — مقصدِ CTA اصلی: مهمان → صفحهٔ ورود، لاگین‌کرده → داشبورد. */
  const primaryCtaLabel = isAuthed ? t("home.cta.dashboard") : t("home.cta.primary");
  const goPrimaryCta = React.useCallback(
    () => go(isAuthed ? BRANDING.authedCta.route : BRANDING.primaryCtaRoute),
    [go, isAuthed],
  );
  // useMouseParallax MUST run unconditionally to satisfy the rules of
  // hooks; the hook itself opts out at runtime when the marketing page
  // isn't mounted (no `.hero-bg .aurora` in the DOM = no work).
  useMouseParallax();

  // R-Landing-v2: reveal-on-scroll IntersectionObserver. Elements with
  // [data-reveal] get .in class when 8% in view. Pulled from the design's
  // app.tsx pattern. Lightweight — no TBT hit at the v1 levels (single
  // observer, no animation clustering).
  React.useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 }
    );
    document.querySelectorAll(".home-shell-v2 [data-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // R-Landing-v2: feature card spotlight follows the mouse. Scoped to
  // .home-shell-v2 .feature elements only (won't fire elsewhere).
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest(".home-shell-v2 .feature") as HTMLElement | null;
      if (!target) return;
      const r = target.getBoundingClientRect();
      target.style.setProperty("--mx", ((e.clientX - r.left) / r.width * 100) + "%");
      target.style.setProperty("--my", ((e.clientY - r.top) / r.height * 100) + "%");
    };
    document.addEventListener("mousemove", handler);
    return () => document.removeEventListener("mousemove", handler);
  }, []);

  // D48 ITEM 2 — mark body so home-v2-overrides.css can hide the AppShell
  // global Nav while Home is mounted. No :has() involved — React lifecycle
  // controls the attribute. D49 supersedes this with a direct AppShell
  // conditional but the data attribute stays as belt-and-suspenders.
  React.useEffect(() => {
    document.body.dataset.homeShell = "true";
    return () => { delete document.body.dataset.homeShell; };
  }, []);

  // D49 ITEM 7 — mobile hamburger state for the home Nav. Below 1024px
  // the nav-actions row collapses behind a hamburger button; tap opens
  // a drawer from the right edge (RTL start edge). Auto-closes on any
  // nav-link tap so the user can navigate immediately.
  const [homeMenuOpen, setHomeMenuOpen] = React.useState(false);
  // S-LAND-VID — همان مرزی که CSS از قدیم روی `.hero-3d` دارد (زیرِ ۱۰۲۴ پنهان
  // است). این‌جا در JS تکرار می‌شود تا روی گوشی به‌جای «هیچ»، فقط کارتِ کلاسِ
  // زنده رندر شود؛ اگر برعکس با CSS پنهان می‌کردیم، مرورگر هر دو نسخهٔ ویدیو را
  // دانلود و پخش می‌کرد.
  const heroDeck = useMediaQuery("(min-width: 1024px)");
  // Close on Escape for keyboard a11y.
  React.useEffect(() => {
    if (!homeMenuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setHomeMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [homeMenuOpen]);

  // Phase-A R7.1.1.a — REVERTED the .is-ready gated-animation pattern.
  // Initial attempt clustered 8+ animations into one trigger moment
  // and Lighthouse counted that concentrated work as a +1290ms TBT
  // spike (440 → 1730). Final shape: the static hero IS the design;
  // no JS-driven animation toggle. The auroras and 3D cards paint
  // in their final state and stay there. (Code intentionally absent.)

  // NOTE: COURSES moved here (was a module-level const at the bottom of the
  // file) so each entry's title/by/desc can be resolved via t() — array
  // literals can't call hooks at module scope. Consumed by the inline
  // "courses" section below (COURSES.map(...)).
  const COURSES = [
    { title: t("home.courses.items.mlFoundations.title"), code: "CS-410", by: t("home.courses.items.mlFoundations.by"), desc: t("home.courses.items.mlFoundations.desc"), weeks: toFa(12), modules: toFa(24), students: toFa(843), cover: "cyan", glyph: "ML" },
    { title: t("home.courses.items.scalableSystems.title"), code: "CS-580", by: t("home.courses.items.scalableSystems.by"), desc: t("home.courses.items.scalableSystems.desc"), weeks: toFa(10), modules: toFa(20), students: toFa(512), cover: "amber", glyph: "SYS" },
    { title: t("home.courses.items.dataProductDesign.title"), code: "PM-310", by: t("home.courses.items.dataProductDesign.by"), desc: t("home.courses.items.dataProductDesign.desc"), weeks: toFa(8), modules: toFa(16), students: toFa(327), cover: "violet", glyph: "PM" },
    { title: t("home.courses.items.advancedNlp.title"), code: "CS-620", by: t("home.courses.items.advancedNlp.by"), desc: t("home.courses.items.advancedNlp.desc"), weeks: toFa(14), modules: toFa(28), students: toFa(624), cover: "deep", glyph: "NLP" },
    { title: t("home.courses.items.aiEthics.title"), code: "PHIL-220", by: t("home.courses.items.aiEthics.by"), desc: t("home.courses.items.aiEthics.desc"), weeks: toFa(6), modules: toFa(12), students: toFa(418), cover: "rose", glyph: "AI⁺" },
    { title: t("home.courses.items.appliedBayesianStats.title"), code: "STAT-440", by: t("home.courses.items.appliedBayesianStats.by"), desc: t("home.courses.items.appliedBayesianStats.desc"), weeks: toFa(10), modules: toFa(20), students: toFa(289), cover: "green", glyph: "Σ" },
  ];

  return (
    <div className="home-shell-v2">
      {/* ============== TOPBAR (Q2.b — Jahad institutional badge) ============== */}
      {/* Scoped INSIDE .home-shell-v2 so it cannot render on any other
          route. position: sticky from home-v2.css makes it stick within
          this wrapper. */}
      <div className="topbar">
        <div className="container topbar-inner">
          <div className="left">
            <span className="badge">
              <Icon name="star" size={14} />
              <b style={{ color: "white", fontWeight: 600, margin: "0 4px" }}>{BRAND.org.nameFa}</b> — {BRAND.org.sloganFa}
            </span>
            <span className="sep" />
            <span className="badge">
              <Icon name="check" size={13} />
              {t("home.topbar.registrationBadge")}
            </span>
          </div>
          <div className="right">
            <a href="mailto:info@digiuniversity.ir">
              <Icon name="globe" size={12} />
              info@digiuniversity.ir
            </a>
            <span className="sep" />
            <span>
              <Icon name="chat" size={12} />
              {t("home.topbar.supportBadge")}
            </span>
          </div>
        </div>
      </div>

      {/* ============== HOME NAV (D48 ITEM 2 — replaces AppShell Nav on /) ============== */}
      {/* AppShell Nav is hidden via body[data-home-shell] data-attribute set
          by the useEffect above. This local nav uses the design's logo +
          brand text and routes to the same public destinations. */}
      <nav className="home-nav-v2" aria-label={t("home.nav.ariaLabel")}>
        <div className="home-nav-inner">
          <div className="home-brand">
            {/* موج ۳ — نوارِ خانه روشن است: نسخهٔ on-light نشان. */}
            <img src={BRAND.assets.markOnLight96} alt={brandNameFull} width="44" height="44" />
            <div className="home-brand-text">
              <span className="home-brand-name">{brandNameFull}</span>
              <span className="home-brand-sub">{t("shell:brand.markSubtitle")}</span>
            </div>
          </div>
          {/* D49 ITEM 7 — mobile hamburger, wrapped with the language switch
              (S-I18N-W1a-FIX) so the pair moves together at the 1024px
              pivot. CSS hides this wrapper above 1024px and hides the
              inline .home-nav-actions below 1024px — but the switch itself
              is rendered only once (gated on the same 1024px breakpoint via
              `heroDeck`), never in both containers at once, or its
              data-testid duplicates in the DOM regardless of which half CSS
              hides. */}
          <div className="home-nav-mobile-controls">
            {!heroDeck && (
              <LanguageSwitcher
                authed={isAuthed}
                enabledLocales={auth.user?.enabledLocales}
                variant="compact"
              />
            )}
            <button
              type="button"
              className="home-nav-burger"
              aria-label={homeMenuOpen ? t("home.nav.closeMenu") : t("home.nav.openMenu")}
              aria-expanded={homeMenuOpen}
              aria-controls="home-nav-drawer"
              onClick={() => setHomeMenuOpen((v) => !v)}
            >
              <span className="burger-line"></span>
              <span className="burger-line"></span>
              <span className="burger-line"></span>
            </button>
          </div>
          <div className="home-nav-actions">
            {/* S-I18N-W1a-FIX — same switch as the mobile entry point, just not both at once. */}
            {heroDeck && (
              <LanguageSwitcher
                authed={isAuthed}
                enabledLocales={auth.user?.enabledLocales}
                variant="compact"
              />
            )}
            <button type="button" className="home-nav-link" onClick={() => go("about")}>
              {t("home.nav.about")}
            </button>
            <button type="button" className="home-nav-link" onClick={() => go("programs")}>
              {t("home.nav.programs")}
            </button>
            {/* م۴ — در حالتِ لاگین، «ورود/ثبت‌نام» جای خود را به یک دکمهٔ
                «داشبورد من» می‌دهد (بدون هیچ دادهٔ هویتیِ کاربر). قیفِ
                پذیرش (/apply) برای مهمان دست‌نخورده می‌ماند. */}
            {isAuthed ? (
              <button type="button" className="home-nav-link is-primary" onClick={goPrimaryCta}>
                {t("home.cta.dashboard")}
              </button>
            ) : (
              <>
                <button type="button" className="home-nav-link" onClick={() => go("login")}>
                  {t("home.nav.login")}
                </button>
                <button type="button" className="home-nav-link is-primary" onClick={() => go("apply")}>
                  {t("home.nav.applyFree")}
                </button>
              </>
            )}
          </div>
        </div>
        {/* Mobile drawer — collapsed at ≥1024 via CSS. */}
        {homeMenuOpen ? (
          <>
            <div
              className="home-nav-backdrop"
              onClick={() => setHomeMenuOpen(false)}
              aria-hidden="true"
            />
            <div id="home-nav-drawer" className="home-nav-drawer" role="dialog" aria-label={t("home.nav.drawerAriaLabel")}>
              <button
                type="button"
                className="home-nav-link"
                onClick={() => { setHomeMenuOpen(false); go("about"); }}
              >
                {t("home.nav.about")}
              </button>
              <button
                type="button"
                className="home-nav-link"
                onClick={() => { setHomeMenuOpen(false); go("programs"); }}
              >
                {t("home.nav.programs")}
              </button>
              {isAuthed ? null : (
                <>
                  <button
                    type="button"
                    className="home-nav-link"
                    onClick={() => { setHomeMenuOpen(false); go("login"); }}
                  >
                    {t("home.nav.login")}
                  </button>
                  <button
                    type="button"
                    className="home-nav-link"
                    onClick={() => { setHomeMenuOpen(false); go("apply"); }}
                  >
                    {t("home.nav.applyFree")}
                  </button>
                </>
              )}
              {/* بند ۹ — CTA «شروع رایگان» داخلِ منوی باز؛ حالتِ لاگین
                  همان دکمه را به «داشبورد من» تبدیل می‌کند. */}
              <button
                type="button"
                className="home-nav-link is-primary"
                onClick={() => { setHomeMenuOpen(false); goPrimaryCta(); }}
              >
                {primaryCtaLabel}
              </button>
            </div>
          </>
        ) : null}
      </nav>

      <main data-screen-label="01 خانه">
      {/* ============== HERO ============== */}
      <section className="hero">
        <div className="hero-bg">
          <div className="aurora aurora-1"></div>
          <div className="aurora aurora-2"></div>
          <div className="aurora aurora-3"></div>
        </div>

        <div className="shell hero-stage">
          <div className="hero-headline">
            {/* S-REBRAND-EMI موج ۳ — بلوکِ کوبرندِ پیشین (hero-crown با دو
                کارتِ لوگو) طبقِ تصمیمِ مالک («حذف کامل کوبرند») برداشته شد؛
                در هیرو فقط برندِ دانشگاه می‌ماند و نامِ مجریِ فنی تنها در
                فوترِ حقوقی می‌آید. */}
            {/* D55/D58 owner directives: به‌جای ایبروِ لاتین، یک لاک‌آپِ بزرگِ
                وسط‌چین. موج ۳ همان جایگاه را نگه می‌دارد و فقط دارایی را به
                لاک‌آپِ EMI عوض می‌کند (نشان + نامِ کامل + نامِ لاتین). */}
            <div className="hero-logo-mark" data-reveal>
              {/* LCP صفحه همین تصویر است: ۷۰۰×۹۲۰ (۲× سقفِ رندرِ ۴۶۰px) و با
                  تخت‌سازیِ رنگ در ژنراتور ۳۲KB مانده — هم‌ردهٔ ۱۹KB وبِ‌پیِ
                  قبلی، بی‌آنکه وابستگیِ npm به ژنراتور اضافه شود.
                  fetchpriority=high + decoding=sync اولویتِ LCP را می‌دهند. */}
              <img
                src={BRAND.assets.heroLockup}
                alt={brandNameFull}
                fetchPriority="high"
                decoding="sync"
                width="700"
                height="920"
              />
            </div>
            {/* S-LAND-POLISH م۲ — بازخوردِ صریحِ مالک: «زیر لوگو، نام …
                فونت و سایزش افتضاح است — باید بزرگ، فاخر و متناسب شود».
                نام تا این لحظه فقط داخلِ alt تصویر بود (نامرئی) و در نوارِ
                بالا با ۱۵px. حالا یک بلوکِ برندِ واقعی زیرِ لاک‌آپ:
                فارسی Vazirmatn وزن ۸۰۰ در clamp(24px,4vw,44px) و زیرش
                سطرِ لاتین با letter-spacing ظریف. هر دو رشته از
                brand.config می‌آیند — هیچ نامی در JSX هاردکد نیست. */}
            <div className="hero-brand-block" data-reveal>
              <p className="hero-brand-name">{brandNameFull}</p>
              <p className="hero-brand-en latin">{BRAND.nameEn}</p>
            </div>
            {/* S-LP-2 (landingpage.md §D.1 بخش ۱) — benefit-first H1 + ONE
                primary CTA (free-plan framing, no card) + a secondary CTA that
                scrolls to the AI demo. Copy comes from the branding config
                (white-label). The D58 logo lockup above is UNTOUCHED. */}
            <h1 className="hero-title">
              <span className="hero-title-line">{t("home.hero.title")}</span>
            </h1>
            <p className="hero-sub">{t("home.hero.subtitle")}</p>
            <div className="hero-cta">
              {/* م۳ — برچسب فقط «شروع رایگان»؛ مقصد صفحهٔ ورود/ثبت‌نام
                  (پیش‌تر فرمِ /apply بود). حالتِ لاگین → داشبورد. */}
              <Button variant="primary" size="lg" className="lp-btn-gold" onClick={goPrimaryCta}>
                {primaryCtaLabel}
                <Icon name="arrow" size={16} />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() =>
                  document.getElementById("lp-ai-demo")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                <Icon name="sparkle" size={16} />
                {t("home.cta.secondary")}
              </Button>
            </div>
          </div>

          {/* 3D layered preview */}
          {/* S-LAND-VID — زیرِ ۱۰۲۴ چیدمانِ چهارکارتی جا نمی‌شود و از قدیم
              `display:none` بود؛ یعنی روی گوشی قابِ کلاسِ زنده اصلاً دیده
              نمی‌شد. حالا همان کارت — و فقط همان — تمام‌عرض زیرِ CTA می‌آید.
              چون شرط در JS است نه CSS، دقیقاً **یک** گرهٔ <video> در صفحه
              وجود دارد: نه دو دانلودِ موازی، نه دو پخشِ هم‌زمان. */}
          {heroDeck ? (
            <div className="hero-3d">
              <Hero3DClassroom />
              <Hero3DTutor />
              <Hero3DAnalytics />
              <Hero3DCredential />
            </div>
          ) : (
            <div className="lp-hero-live-mobile">
              <Hero3DClassroom />
            </div>
          )}

          {/* S-LP-2 — the credibility strip reads its numbers from the
              branding config (white-label §D.5), Persian digits via toFa. */}
          <div className="hero-stats">
            <Stat v={toFa(BRANDING.stats.faculties)} unit={t("home.hero.stats.faculties.unit")} l={t("home.hero.stats.faculties.label")} />
            <Stat v={toFa(BRANDING.stats.programs)} unit={t("home.hero.stats.programs.unit")} l={t("home.hero.stats.programs.label")} />
            <Stat v={toFa(BRANDING.stats.instructors)} unit={t("home.hero.stats.instructors.unit")} l={t("home.hero.stats.instructors.label")} />
            <Stat v={formatNumberFa(BRANDING.stats.students)} unit={t("home.hero.stats.students.unit")} l={t("home.hero.stats.students.label")} />
          </div>
        </div>
      </section>

      {/* ============== TRUST STRIP (R9 §2) — REMOVED per D54 owner directive.
           Owner explicit «remove this section in landing page» pointing at the
           «سازمان‌های همکار و دانشگاه‌های شریک» strip with MSA/UT/Sharif/KHU/IUT
           /Amirkabir tiles. The structured D53 <PartnersV2Section /> below
           (rendered further down the page) provides the same affiliations
           with the visual treatment owner approved. TrustStrip definition is
           preserved at line ~1043 for potential reuse but not rendered. */}

      {/* ============== S-LP بخش ۲ — نوار اعتماد رسمی ============== */}
      <L><TrustBar /></L>

      {/* ============== STATS BAND (R9 §3) ============== */}
      <StatsBand />

      {/* ============== S-LP بخش ۳ — مشکل ← راه‌حل ============== */}
      <L><ProblemSolutionSection /></L>

      {/* ====== بخش ۳٫۵ — «این پلتفرم دقیقاً چیست؟» (ILP / TIP / چندعامله)
           خواستهٔ صریحِ مالک؛ عمداً بلافاصله پس از «مشکل ← راه‌حل» و پیش از
           دمو می‌آید: اول تعریفِ علمیِ سامانه، بعد دیدنِ آن در عمل. ====== */}
      <L><PlatformArchitectureSection /></L>

      {/* ============== S-LP بخش ۴ ⭐ — دموی تعاملی دستیارها ============== */}
      <L><AiDemoSection go={go} /></L>

      {/* ============== AGENT SYSTEM ============== */}
      {/* S-LAND-POLISH — این بخش تا امروز هیچ کلاسِ یکتایی نداشت و از CSS
          قابلِ هدف‌گیری نبود؛ `emi-arch` فقط یک هوکِ استایل است. */}
      <section className="section emi-arch">
        <div className="shell">
          <div className="section-head reveal">
            <div className="text">
              <span className="eyebrow">{t("home.agentSystem.eyebrow")}</span>
              <h2 className="h-1">
                {t("home.agentSystem.titleLine1")}
                <br />
                <span style={{ color: "var(--violet)" }}>{t("home.agentSystem.titleHighlight")}</span>
              </h2>
            </div>
            <p className="lead">
              {t("home.agentSystem.lead")}
            </p>
          </div>

          <div className="pillar-grid stagger">
            {/* Pillar 1 — agent graph */}
            <div className="pillar pillar-1 reveal">
              <div className="glow"></div>
              <span className="num">{t("home.agentSystem.pillar1.num")}</span>
              <h3>{t("home.agentSystem.pillar1.title")}</h3>
              <p>{t("home.agentSystem.pillar1.desc")}</p>
              <div className="agent-graph">
                {[
                  ["TUTOR", t("home.agentSystem.roles.tutor")],
                  ["COACH", t("home.agentSystem.roles.coach")],
                  ["CRITIC", t("home.agentSystem.roles.critic")],
                  ["MENTOR", t("home.agentSystem.roles.mentor")],
                  ["GRADER", t("home.agentSystem.roles.grader")],
                  ["HUMAN", t("home.agentSystem.roles.human")],
                ].map(([k, l]) => (
                  <div key={k} className="agent-node">
                    <div className="nm">{k}</div>
                    <div className="lb">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pillar pillar-2 reveal">
              <div className="glow"></div>
              <span className="num">{t("home.agentSystem.pillar2.num")}</span>
              <h3>{t("home.agentSystem.pillar2.title")}</h3>
              <p>{t("home.agentSystem.pillar2.desc")}</p>
              <div className="pt-5 flex gap-2.5 flex-wrap"  style={{marginTop: "auto"}}>
                <span className="pill pill-cyan">Bayesian KT</span>
                <span className="pill pill-amber">Deep KT</span>
                <span className="pill">IRT 2PL</span>
              </div>
            </div>

            <div className="pillar pillar-3 reveal">
              <div className="glow"></div>
              <span className="num">{t("home.agentSystem.pillar3.num")}</span>
              <h3>{t("home.agentSystem.pillar3.title")}</h3>
              <p>{t("home.agentSystem.pillar3.desc")}</p>
              <div className="pt-5"  style={{marginTop: "auto"}}>
                <MasteryRing percent={78} />
              </div>
            </div>

            <div className="pillar pillar-4 reveal">
              <div className="glow"></div>
              <span className="num">{t("home.agentSystem.pillar4.num")}</span>
              <h3>{t("home.agentSystem.pillar4.title")}</h3>
              <p>{t("home.agentSystem.pillar4.desc")}</p>
            </div>

            <div className="pillar pillar-5 reveal">
              <div className="glow"></div>
              <span className="num">{t("home.agentSystem.pillar5.num")}</span>
              <h3>{t("home.agentSystem.pillar5.title")}</h3>
              <p>{t("home.agentSystem.pillar5.desc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== S-LP بخش ۵ — مسیر سفر ۵ قدمی ============== */}
      <L><JourneySection /></L>

      {/* ============== S-LP بخش ۶ — برنامه‌ها با فیلتر مقطع (دادهٔ واقعی) ============== */}
      <L><ProgramsExplorerSection go={go} /></L>

      {/* ============== CATALOG TEASER (R10 §5) ============== */}
      <CatalogTeaser go={go} />

      {/* ============== FACULTY SHOWCASE (R10 §4) ============== */}
      {/* D48 ITEM 4 — Faculty section per design (8 portraits + data verbatim). */}
      <FacultyV2Section />

      {/* ============== S-LP بخش ۷ — کلاس زندهٔ خودمیزبان ============== */}
      <L><LiveClassSection go={go} /></L>

      {/* ============== S-LP بخش ۸ — پلن‌ها (دادهٔ واقعی /pricing) ============== */}
      <L><PricingSection go={go} /></L>

      {/* ============== S-LP بخش ۹ — راستی‌آزمایی گواهی ============== */}
      <L><CredentialVerifySection go={go} /></L>

      {/* ============== S-LP بخش ۱۰ — بازارگاه دانش / مدرس شو ============== */}
      <L><MarketplaceSection go={go} /></L>

      {/* ============== TESTIMONIALS (R10 §6) — اثبات اجتماعی ============== */}
      <TestiV2Section />

      {/* ============== PARTNERS V2 (D53) — owner: «جذاب نیست درستش کن».
           Replaces the plain scrolling-text marquee with a structured
           card-grid section organized into 3 categories. Each card shows
           a stylized mark (initials in colored circle) + full name +
           sublabel. Hover lift + accent border. */}
      <PartnersV2Section />


      {/* ============== KNOWLEDGE GRAPH ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="grid grid-2 gap-12 items-center"  style={{gridTemplateColumns: "1fr 1.1fr"}}>
            <div>
              <span className="eyebrow">{t("home.knowledgeGraph.eyebrow")}</span>
              <h2 className="h-1 mt-4" data-testid="home-knowledge-title">
                <Trans t={t} i18nKey="home.knowledgeGraph.titleSentence" components={{ lead: <span style={{ display: "block" }} />, highlight: <span style={{ color: "var(--cyan)" }} /> }} />
              </h2>
              <p className="lead mt-5" >
                {t("home.knowledgeGraph.lead")}
              </p>
              <div className="grid mt-8 gap-4.5"  style={{gridTemplateColumns: "1fr 1fr"}}>
                <Feature title={t("home.knowledgeGraph.features.hybridRetrieval.title")} desc={t("home.knowledgeGraph.features.hybridRetrieval.desc")} icon="search" />
                <Feature title={t("home.knowledgeGraph.features.secondJump.title")} desc={t("home.knowledgeGraph.features.secondJump.desc")} icon="play" />
                <Feature title={t("home.knowledgeGraph.features.conceptExtraction.title")} desc={t("home.knowledgeGraph.features.conceptExtraction.desc")} icon="sparkle" />
                <Feature title={t("home.knowledgeGraph.features.remediationPath.title")} desc={t("home.knowledgeGraph.features.remediationPath.desc")} icon="target" />
              </div>
            </div>

            <div className="kg-wrap">
              <KnowledgeGraph />
            </div>
          </div>
        </div>
      </section>

      {/* ============== ARCHITECTURE ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="grid grid-2 gap-12 items-center"  style={{gridTemplateColumns: "1.1fr 1fr"}}>
            <div className="kg-wrap grid"  style={{minHeight: 480, placeItems: "center"}}>
              <ArchStack />
            </div>
            <div>
              <span className="eyebrow">{t("home.architecture.eyebrow")}</span>
              <h2 className="h-1 mt-4" >
                {t("home.architecture.titlePrefix")} <span style={{ color: "var(--amber)" }}>{t("home.architecture.titleHighlight")}</span>{t("home.architecture.titleComma")}
                <br />
                {t("home.architecture.titleSuffix")}
              </h2>
              <p className="lead mt-5" >
                {t("home.architecture.lead")}
              </p>
              <div className="mt-7" >
                <TechRow label="Frontend" items={["Next.js 15", "TypeScript", "Tailwind", "WebRTC client"]} />
                <TechRow label="Backend" items={["NestJS", "Spring Boot", "FastAPI", "gRPC"]} />
                <TechRow label="AI & Data" items={["LangGraph", "Qdrant", "Whisper", "MLflow"]} />
                <TechRow label="Infra" items={["Kubernetes", "Kafka", "ClickHouse", "ArgoCD"]} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== COURSES ============== */}
      {/* S-LAND-POLISH بند ۷ — هوکِ `emi-courses` برای متراکم‌کردنِ شبکهٔ
          دوره‌ها (۳ ستونِ پهن ⇒ auto-fill + پلهٔ تبلت). */}
      <section className="section emi-courses">
        <div className="shell">
          <div className="section-head">
            <div className="text">
              <span className="eyebrow">{t("home.courses.eyebrow")}</span>
              <h2 className="h-1 mt-4" >{t("home.courses.title")}</h2>
            </div>
            <Button variant="outline" onClick={() => go("programs")}>
              {t("home.courses.viewAllButton")}
              <Icon name="arrow" size={14} />
            </Button>
          </div>

          <Stagger className="grid grid-3">
            {COURSES.map((c) => (
              <button
                key={c.title}
                type="button"
                className="course-card reveal"
                onClick={() => go("course")}
                aria-label={t("home.courses.cardAriaLabel", { title: c.title, by: c.by })}
              >
                <div className={"course-cover " + c.cover}>
                  <span className="glyph">{c.glyph}</span>
                  <span className="pill mono">{c.code}</span>
                </div>
                <div className="course-body">
                  {/* R7.3 A.3.i — was `<h4>` directly under section's
                      `<h2>` ("دروسی که با تو رشد می‌کنند") which skips
                      h3 (heading-order fail). Renamed to `<h3>`;
                      `.course-body h4` rule in styles.css updated to
                      `.course-body h3` to keep the visual treatment. */}
                  <h3>{c.title}</h3>
                  <div className="by">{t("home.courses.byPrefix", { by: c.by })}</div>
                  <p style={{ fontSize: 13, color: "var(--fg-mute)", lineHeight: 1.6 }}>{c.desc}</p>
                  <div className="course-meta">
                    <span className="it"><strong>{c.weeks}</strong> {t("home.courses.weeksUnit")}</span>
                    <span className="it"><strong>{c.modules}</strong> {t("home.courses.modulesUnit")}</span>
                    <span className="it"><strong>{c.students}</strong> {t("home.courses.studentsUnit")}</span>
                  </div>
                </div>
              </button>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============== STANDARDS ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="card p-12 text-center" >
            <span className="eyebrow justify-center" >STANDARDS · INTEROPERABILITY</span>
            <h2 className="h-2 mt-4.5"  style={{ maxWidth: 720, margin: "18px auto 0"}}>
              {t("home.standards.heading")}
            </h2>
            <div className="standards justify-center mt-8" >
              <span className="std">LTI 1.3 / Advantage</span>
              <span className="std">xAPI · cmi5</span>
              <span className="std">QTI 3.0</span>
              <span className="std">Caliper Analytics</span>
              <span className="std">OneRoster 1.2</span>
              <span className="std">Open Badges 3.0</span>
              <span className="std">Verifiable Credentials</span>
              <span className="std">WCAG 2.2 AA</span>
              <span className="std">SCORM legacy</span>
              <span className="std">SAML 2.0 / OIDC</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PLATFORM TOUR ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="section-head">
            <div className="text">
              <span className="eyebrow">{t("home.platformTour.eyebrow")}</span>
              <h2 className="h-1 mt-4" >{t("home.platformTour.title")}</h2>
            </div>
            <p className="lead">
              {t("home.platformTour.lead")}
            </p>
          </div>

          <Stagger className="grid grid-4">
            {[
              { id: "classroom", t: t("home.platformTour.items.classroom.title"), d: t("home.platformTour.items.classroom.desc"), ic: "live" },
              { id: "labs", t: t("home.platformTour.items.labs.title"), d: t("home.platformTour.items.labs.desc"), ic: "flask" },
              { id: "schools", t: t("home.platformTour.items.schools.title"), d: t("home.platformTour.items.schools.desc"), ic: "grad" },
              { id: "research", t: t("home.platformTour.items.research.title"), d: t("home.platformTour.items.research.desc"), ic: "flask" },
              { id: "dashboard", t: t("home.platformTour.items.dashboard.title"), d: t("home.platformTour.items.dashboard.desc"), ic: "home" },
              { id: "course", t: t("home.platformTour.items.course.title"), d: t("home.platformTour.items.course.desc"), ic: "book" },
              { id: "search", t: t("home.platformTour.items.search.title"), d: t("home.platformTour.items.search.desc"), ic: "search" },
              { id: "calendar", t: t("home.platformTour.items.calendar.title"), d: t("home.platformTour.items.calendar.desc"), ic: "calendar" },
              { id: "library", t: t("home.platformTour.items.library.title"), d: t("home.platformTour.items.library.desc"), ic: "folder" },
              { id: "assessment", t: t("home.platformTour.items.assessment.title"), d: t("home.platformTour.items.assessment.desc"), ic: "check" },
              { id: "community", t: t("home.platformTour.items.community.title"), d: t("home.platformTour.items.community.desc"), ic: "users" },
              { id: "recordings", t: t("home.platformTour.items.recordings.title"), d: t("home.platformTour.items.recordings.desc"), ic: "video" },
              { id: "instructor", t: t("home.platformTour.items.instructor.title"), d: t("home.platformTour.items.instructor.desc"), ic: "chip" },
              { id: "authoring", t: t("home.platformTour.items.authoring.title"), d: t("home.platformTour.items.authoring.desc"), ic: "sparkle" },
              { id: "analytics", t: t("home.platformTour.items.analytics.title"), d: t("home.platformTour.items.analytics.desc"), ic: "chart" },
              { id: "admin", t: t("home.platformTour.items.admin.title"), d: t("home.platformTour.items.admin.desc"), ic: "settings" },
              { id: "parent", t: t("home.platformTour.items.parent.title"), d: t("home.platformTour.items.parent.desc"), ic: "users" },
              { id: "officehours", t: t("home.platformTour.items.officehours.title"), d: t("home.platformTour.items.officehours.desc"), ic: "calendar" },
              { id: "events", t: t("home.platformTour.items.events.title"), d: t("home.platformTour.items.events.desc"), ic: "live" },
              { id: "faculty", t: t("home.platformTour.items.faculty.title"), d: t("home.platformTour.items.faculty.desc"), ic: "grad" },
              { id: "help", t: t("home.platformTour.items.help.title"), d: t("home.platformTour.items.help.desc"), ic: "headset" },
              { id: "pricing", t: t("home.platformTour.items.pricing.title"), d: t("home.platformTour.items.pricing.desc"), ic: "dollar" },
              { id: "credential", t: t("home.platformTour.items.credential.title"), d: t("home.platformTour.items.credential.desc"), ic: "cert" },
              { id: "about", t: t("home.platformTour.items.about.title"), d: t("home.platformTour.items.about.desc"), ic: "sparkle" },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                className="reveal rounded-xl p-5 cursor-pointer relative overflow-hidden text-right"
                onClick={() => go(p.id)}
                aria-label={`${p.t} · ${p.d}`}
                style={{background: "var(--surface)",
                border: "1px solid var(--line)",
                transition: "200ms ease",
                fontFamily: "inherit",
                color: "var(--fg)"}}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "var(--accent)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "var(--line)"; }}
              >
                <div className="flex items-center justify-between mb-3.5" >
                  <div className="rounded-lg grid"  style={{width: 36, height: 36, background: "var(--surface-2)", color: "var(--accent)", placeItems: "center"}}>
                    <Icon name={p.ic} size={16} />
                  </div>
                  <Icon name="arrow" size={14} stroke={2} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{p.t}</div>
                <div className="mt-1.5"  style={{fontSize: 12, color: "var(--fg-mute)", lineHeight: 1.5}}>{p.d}</div>
              </button>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============== S-UX-WAVE1 — قابلیت‌های ساخته‌شده و تا این بسته
          روی لندینگ نبودند (ledger §۴.۱). جای مجاز افزودن دقیقاً همین‌جاست:
          بعد از گریدِ تورِ پلتفرم، پیش از بخشِ سؤالاتِ متداول. ============== */}
      <L><FeaturesSection go={go} /></L>

      {/* ============== S-LP بخش ۱۲ — سؤالات متداول + FAQPage schema ============== */}
      <L><FaqSection /></L>

      {/* ============== CTA (نهادی — حفظ‌شده) ============== */}
      <section className="section emi-instcta">
        <div className="shell">
          <div className="card grid gap-12 items-center"  style={{padding: "64px 48px",
            background: "linear-gradient(135deg, var(--surface) 0%, color-mix(in oklch, var(--cyan) 8%, var(--surface-2)) 100%)", gridTemplateColumns: "1.4fr 1fr"}}>
            <div>
              <span className="eyebrow">{t("home.institutionalCta.eyebrow")}</span>
              <h2 className="h-1 mt-4" >{t("home.institutionalCta.title")}</h2>
              <p className="lead mt-4" >
                {t("home.institutionalCta.lead")}
              </p>
              <div className="flex gap-3 mt-8" >
                <Button variant="primary" size="lg" onClick={() => go("apply")}>
                  {t("home.institutionalCta.demoButton")}
                  <Icon name="arrow" size={16} />
                </Button>
                <Button variant="outline" size="lg">
                  <Icon name="download" size={16} />
                  {t("home.institutionalCta.proposalButton")}
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-2.5" >
              {[
                [t("home.institutionalCta.milestones.mvp.title"), t("home.institutionalCta.milestones.mvp.sub")],
                [t("home.institutionalCta.milestones.fullTerm.title"), t("home.institutionalCta.milestones.fullTerm.sub")],
                [t("home.institutionalCta.milestones.nationalScale.title"), t("home.institutionalCta.milestones.nationalScale.sub")],
              ].map(([mt, ms], i) => (
                <div key={i} className="card-flat flex items-center gap-3.5" >
                  <div className="rounded-xl grid"  style={{width: 36, height: 36, background: "var(--surface-3)", color: "var(--cyan)", placeItems: "center", fontFamily: "var(--f-mono)", fontWeight: 700, fontSize: 13}}>
                    {toFa(i + 1)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{mt}</div>
                    <div className="mt-0.5"  style={{color: "var(--fg-mute)", fontSize: 12}}>{ms}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== S-LP بخش ۱۳ — CTA نهایی ============== */}
      <L><FinalCtaSection go={go} /></L>
      </main>

      {/* S-LP — CTA چسبان موبایل (فقط <768px، تپ‌تارگت ≥۴۴px) */}
      <L><StickyMobileCta go={go} /></L>
    </div>
  );
};

// ====== sub-components for home ======

// D48 ITEM 4 — Faculty section per Claude Design data.tsx verbatim.
// 8 portraits served from /landing-v2/faculty/. Data preserves design's
// exact names, roles, and fields. Layout: 4-col desktop / 2-col tablet
// / 1-col mobile (CSS in home-v2-overrides.css .faculty-v2-grid).
// R7.1.5.b — portrait PNGs converted to optimized JPGs via sharp
// (`scripts/optimize-landing-images.mjs` author-time one-off).
// Total portrait weight: 11.4 MB → ~250 KB (-97%). Visual quality
// preserved at 600w + JPG quality 82 + mozjpeg encoder. See decision
// log D57 for the rationale.
const FacultyV2Section = () => {
  const { t } = useTranslation("landing");
  const FACULTY_V2 = [
    { name: t("home.faculty.members.hosseini.name"), role: t("home.faculty.members.hosseini.role"), field: t("home.faculty.members.hosseini.field"), photo: "/landing-v2/faculty/m2.webp" },
    { name: t("home.faculty.members.karimi.name"),   role: t("home.faculty.members.karimi.role"),   field: t("home.faculty.members.karimi.field"),   photo: "/landing-v2/faculty/w1.webp" },
    { name: t("home.faculty.members.mozafari.name"), role: t("home.faculty.members.mozafari.role"), field: t("home.faculty.members.mozafari.field"), photo: "/landing-v2/faculty/m1.webp" },
    { name: t("home.faculty.members.bagheri.name"),  role: t("home.faculty.members.bagheri.role"),  field: t("home.faculty.members.bagheri.field"),  photo: "/landing-v2/faculty/w2.webp" },
    { name: t("home.faculty.members.taheri.name"),   role: t("home.faculty.members.taheri.role"),   field: t("home.faculty.members.taheri.field"),   photo: "/landing-v2/faculty/m3.webp" },
    { name: t("home.faculty.members.moradi.name"),   role: t("home.faculty.members.moradi.role"),   field: t("home.faculty.members.moradi.field"),   photo: "/landing-v2/faculty/w3.webp" },
    { name: t("home.faculty.members.asadi.name"),    role: t("home.faculty.members.asadi.role"),    field: t("home.faculty.members.asadi.field"),    photo: "/landing-v2/faculty/m4.webp" },
    { name: t("home.faculty.members.nasiri.name"),   role: t("home.faculty.members.nasiri.role"),   field: t("home.faculty.members.nasiri.field"),   photo: "/landing-v2/faculty/w4.webp" },
  ];
  return (
  <section className="faculty-v2-section" data-reveal>
    <div className="shell">
      <div className="section-head">
        <div>
          <div className="section-eyebrow">{t("home.faculty.eyebrow")}</div>
          <h2 className="section-title">
            {t("home.faculty.titlePrefix")} <span className="em">{t("home.faculty.titleHighlight")}</span>{t("home.faculty.titleSuffix")}
          </h2>
        </div>
        <p className="section-lead">
          {t("home.faculty.lead")}
        </p>
      </div>
      <div className="faculty-v2-grid">
        {FACULTY_V2.map((f) => (
          <article key={f.name} className="faculty-v2-card">
            <div className="portrait">
              {/* R7.1.5.a — explicit decoding=async + fetchpriority=low
                  so lazy faculty PNGs (1–2 MB each) don't compete with
                  hero LCP candidate for main thread time. */}
              <img
                src={f.photo}
                alt={f.name}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                width="320"
                height="427"
              />
            </div>
            <div className="info">
              <div className="name">{f.name}</div>
              <div className="role">{f.role}</div>
              <div className="field">{f.field}</div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

const Stat = ({ v, unit, l }) => (
  <div className="hero-stat">
    <div className="v">{v}{unit && <span className="unit">{unit}</span>}</div>
    <div className="l">{l}</div>
  </div>
);

// D48 ITEM 5 — Testimonials per Claude Design data.tsx verbatim.
// 3 student stories with circular avatar (photo reused from faculty
// portraits per design's pattern — the design also reuses these as
// generic student avatars). Initials are the text fallback if photo
// fails to load. Layout: 3-col desktop / 2-col tablet (≤1024) / 1-col
// mobile (≤640) via .testi-v2-grid in home-v2-overrides.css.
// D51 ITEM C — testimonials avatars upgraded. Removed faculty-photo reuse
// (owner reported the faculty headshots looked off as "student" avatars).
// Replaced with stylized initial-letter avatars on gradient circles +
// inline graduation-cap SVG decoration in each card. Professional,
// student-relevant, no external asset dependency.
// D52 ITEM 2 — owner directive «تجربه واقعی از دانشجویان ما از عکسهایی
// که تو اپلود فولدر گذاشتم استفاده کنه» — use the real student photos
// from docs/my-upload/landing-v2/uploads/. Each card now has a real
// portrait + initial-letter fallback if image fails.
// NOTE: TESTI_V2 moved inside TestiV2Section (below) so each entry's body/
// name/role can be resolved via t() — array literals can't call hooks at
// module scope. `initials` stays hardcoded Persian (not user-visible: every
// entry always has a `photo`, so the initials fallback branch never renders;
// left untouched intentionally — flagged for merge review).

// Small inline grad-cap SVG used as a decorative element in each testimonial
// card. Single-color, currentColor-aware, ~28px square.
const GradCapSvg = () => (
  <svg
    viewBox="0 0 24 24"
    width="28"
    height="28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m12 4 10 5-10 5L2 9z" />
    <path d="M6 11v4a6 6 0 0 0 12 0v-4" />
    <path d="M21 10v6" />
  </svg>
);

// D53 — Partners section per owner directive «سازمان‌های همکار و
// دانشگاه‌های شریک: جذاب نیست درستش کن». Replaces the plain scrolling
// marquee with a 3-category grid: Iranian top universities, research
// institutes, international partners. Each entry has a styled monogram
// mark + name + sublabel. Hover lift + accent border.
type PartnerEntry = { mark: string; name: string; sub: string; accent: "navy" | "cobalt" | "gold" };

// NOTE: PARTNERS_UNIV / PARTNERS_INSTITUTE moved inside PartnersV2Section so
// each entry's name/sub can be resolved via t() — array literals can't call
// hooks at module scope. PARTNERS_INTL stays at module scope untouched: its
// entries are pure English (proper nouns of foreign partner orgs).
const PARTNERS_INTL: PartnerEntry[] = [
  { mark: "S", name: "Stanford Online", sub: "United States", accent: "gold" },
  { mark: "M", name: "MIT OpenCourseWare", sub: "United States", accent: "gold" },
  { mark: "e", name: "edX Consortium", sub: "Global · OpenEdX", accent: "gold" },
  { mark: "C", name: "Coursera Partners", sub: "Global", accent: "gold" },
];

const PartnersV2Section = () => {
  const { t } = useTranslation("landing");
  const PARTNERS_UNIV: PartnerEntry[] = [
    { mark: t("home.partners.univ.ut.mark"), name: t("home.partners.univ.ut.name"), sub: t("home.partners.univ.ut.sub"), accent: "navy" },
    { mark: t("home.partners.univ.sharif.mark"), name: t("home.partners.univ.sharif.name"), sub: t("home.partners.univ.sharif.sub"), accent: "navy" },
    { mark: t("home.partners.univ.amirkabir.mark"), name: t("home.partners.univ.amirkabir.name"), sub: t("home.partners.univ.amirkabir.sub"), accent: "navy" },
    { mark: t("home.partners.univ.iust.mark"), name: t("home.partners.univ.iust.name"), sub: t("home.partners.univ.iust.sub"), accent: "navy" },
    { mark: t("home.partners.univ.sbu.mark"), name: t("home.partners.univ.sbu.name"), sub: t("home.partners.univ.sbu.sub"), accent: "navy" },
    { mark: t("home.partners.univ.kntu.mark"), name: t("home.partners.univ.kntu.name"), sub: t("home.partners.univ.kntu.sub"), accent: "navy" },
    { mark: t("home.partners.univ.atu.mark"), name: t("home.partners.univ.atu.name"), sub: t("home.partners.univ.atu.sub"), accent: "navy" },
    { mark: t("home.partners.univ.tmu.mark"), name: t("home.partners.univ.tmu.name"), sub: t("home.partners.univ.tmu.sub"), accent: "navy" },
  ];

  const PARTNERS_INSTITUTE: PartnerEntry[] = [
    { mark: t("home.partners.institute.msrt.mark"), name: t("home.partners.institute.msrt.name"), sub: t("home.partners.institute.msrt.sub"), accent: "cobalt" },
    { mark: t("home.partners.institute.ipm.mark"), name: t("home.partners.institute.ipm.name"), sub: t("home.partners.institute.ipm.sub"), accent: "cobalt" },
    { mark: t("home.partners.institute.bmn.mark"), name: t("home.partners.institute.bmn.name"), sub: t("home.partners.institute.bmn.sub"), accent: "cobalt" },
    { mark: t("home.partners.institute.inif.mark"), name: t("home.partners.institute.inif.name"), sub: t("home.partners.institute.inif.sub"), accent: "cobalt" },
    { mark: t("home.partners.institute.mict.mark"), name: t("home.partners.institute.mict.name"), sub: t("home.partners.institute.mict.sub"), accent: "cobalt" },
  ];

  return (
    <section className="partners-v2-section" data-reveal>
      <div className="shell">
        <div className="section-head">
          <div>
            <div className="section-eyebrow">{t("home.partners.eyebrow")}</div>
            <h2 className="section-title">
              {t("home.partners.titlePrefix")} <span className="em">{t("home.partners.titleHighlight")}</span> {t("home.partners.titleSuffix")}
            </h2>
          </div>
          <p className="section-lead">{t("home.partners.lead")}</p>
        </div>

        <div className="partners-v2-group">
          <h3 className="partners-v2-group-title">{t("home.partners.groups.universities")}</h3>
          <div className="partners-v2-grid">
            {PARTNERS_UNIV.map((p) => (
              <PartnerCard key={p.name} {...p} />
            ))}
          </div>
        </div>

        <div className="partners-v2-group">
          <h3 className="partners-v2-group-title">{t("home.partners.groups.institutes")}</h3>
          <div className="partners-v2-grid">
            {PARTNERS_INSTITUTE.map((p) => (
              <PartnerCard key={p.name} {...p} />
            ))}
          </div>
        </div>

        <div className="partners-v2-group">
          <h3 className="partners-v2-group-title">{t("home.partners.groups.international")}</h3>
          <div className="partners-v2-grid">
            {PARTNERS_INTL.map((p) => (
              <PartnerCard key={p.name} {...p} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PartnerCard = ({ mark, name, sub, accent }: PartnerEntry) => (
  <article className={`partner-v2-card accent-${accent}`}>
    <div className="partner-mark" aria-hidden="true">
      <span>{mark}</span>
    </div>
    <div className="partner-info">
      <div className="partner-name">{name}</div>
      <div className="partner-sub">{sub}</div>
    </div>
  </article>
);

const TestiV2Section = () => {
  const { t } = useTranslation("landing");
  const TESTI_V2 = [
    {
      body: t("home.testimonials.items.hosseinRezaei.body"),
      name: t("home.testimonials.items.hosseinRezaei.name"),
      role: t("home.testimonials.items.hosseinRezaei.role"),
      initials: t("home.testimonials.items.hosseinRezaei.initials"),
      accent: "navy",
      /* S-LP-6 — students/*.jpg were byte-identical duplicates of the
         faculty portraits at second URLs (double download). Testimonial
         avatars now reference the single WebP set; the students/ files
         stay on disk untouched. */
      photo: "/landing-v2/faculty/m1.webp",
    },
    {
      body: t("home.testimonials.items.negarSafari.body"),
      name: t("home.testimonials.items.negarSafari.name"),
      role: t("home.testimonials.items.negarSafari.role"),
      initials: t("home.testimonials.items.negarSafari.initials"),
      accent: "cobalt",
      photo: "/landing-v2/faculty/w1.webp",
    },
    {
      body: t("home.testimonials.items.fatemehAhmadi.body"),
      name: t("home.testimonials.items.fatemehAhmadi.name"),
      role: t("home.testimonials.items.fatemehAhmadi.role"),
      initials: t("home.testimonials.items.fatemehAhmadi.initials"),
      accent: "gold",
      photo: "/landing-v2/faculty/w3.webp",
    },
  ];
  return (
  <section className="testi-v2-section" data-reveal>
    <div className="shell">
      <div className="section-head">
        <div>
          <div className="section-eyebrow">{t("home.testimonials.eyebrow")}</div>
          <h2 className="section-title">
            <span className="em">{t("home.testimonials.titleHighlight")}</span> {t("home.testimonials.titleSuffix")}
          </h2>
        </div>
        <p className="section-lead">
          {t("home.testimonials.lead")}
        </p>
      </div>
      <div className="testi-v2-grid">
        {TESTI_V2.map((item) => (
          <article key={item.name} className={`testi-v2-card accent-${item.accent}`}>
            <div className="testi-card-top">
              <div className="quote-mark" aria-hidden="true">&ldquo;</div>
              <div className="testi-card-grad" aria-hidden="true"><GradCapSvg /></div>
            </div>
            <p className="quote-body">{item.body}</p>
            <div className="person">
              <div className="person-avatar" aria-hidden="true">
                {item.photo ? (
                  <img
                    src={item.photo}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    width="56"
                    height="56"
                  />
                ) : (
                  <span>{item.initials}</span>
                )}
              </div>
              <div className="person-info">
                <span className="person-name">{item.name}</span>
                <span className="person-role">{item.role}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

const Feature = ({ title, desc, icon }) => (
  <div>
    <div className="flex items-center gap-2.5 mb-2"  style={{ color: "var(--cyan)"}}>
      <Icon name={icon} size={18} />
      <div style={{ fontWeight: 600, color: "var(--fg)", fontSize: 14 }}>{title}</div>
    </div>
    <div style={{ fontSize: 13, color: "var(--fg-mute)", lineHeight: 1.6 }}>{desc}</div>
  </div>
);

const TechRow = ({ label, items }) => (
  <div className="grid gap-4.5"  style={{ gridTemplateColumns: "100px 1fr", padding: "12px 0", borderTop: "1px solid var(--line)"}}>
    <div className="uppercase"  style={{fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--fg-mute)", letterSpacing: "0.1em"}}>{label}</div>
    <div className="flex flex-wrap gap-1.5" >
      {items.map((i) => <span className="rounded-md" key={i}  style={{fontFamily: "var(--f-mono)", fontSize: 12, padding: "3px 9px", background: "var(--surface-2)", border: "1px solid var(--line)"}}>{i}</span>)}
    </div>
  </div>
);

// Mastery ring for pillar 3
const MasteryRing = ({ percent }) => {
  const { t } = useTranslation("landing");
  const r = 36, c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  return (
    <div className="flex items-center gap-4.5" >
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none" stroke="var(--surface-3)" strokeWidth="6" />
        <circle cx="45" cy="45" r={r} fill="none" stroke="var(--violet)" strokeWidth="6"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          transform="rotate(-90 45 45)" />
        <text x="45" y="50" textAnchor="middle" fill="var(--fg)" fontFamily="var(--f-mono)" fontWeight="700" fontSize="18">{toFa(percent)}٪</text>
      </svg>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{t("home.masteryRing.currentLabel")}</div>
        <div className="mt-1"  style={{fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--fg-mute)"}}>{t("home.masteryRing.goalsProgress")}</div>
      </div>
    </div>
  );
};

// ====== 3D hero floating cards ======

// S-LAND-POLISH م۵ — قابِ LIVE پیش‌تر یک مستطیلِ راه‌راهِ خالی بود. مالک
// خواست داخلش «ویدیو/انیمیشنِ بهینه و باکیفیت» بنشیند. ضبطِ واقعیِ UI
// کلاسِ زنده در این نشست ممکن نبود (اجرا فقط روی VPS است)، پس طبقِ بندِ
// جایگزینِ خودِ دستور، صحنه به‌صورتِ برداری با DOM+CSS ساخته شد:
//   * صفرِ بایت روی باندلِ JS و صفرِ درخواستِ شبکه (نه MP4، نه GIF)،
//   * در هر DPI تیز (برخلافِ ویدئوی ۱.۵ مگابایتی که روی رتینا نرم می‌شود)،
//   * همهٔ حرکت‌ها با prefers-reduced-motion در landing-emi.css خاموش می‌شوند.
// اگر بعداً ضبطِ واقعی آماده شد، همین گره با یک <video muted loop
// playsinline poster …> جایگزین می‌شود و CSS دست‌نخورده می‌ماند.
/* بازخوردِ مالک (عکس ۲) — پیش‌تر شش تایلِ ۲۷پیکسلی با حرفِ ۹پیکسلی بودند
   که «حرفِ ریزِ شکسته» دیده می‌شد. حالا چهار تایلِ بزرگ‌ترِ ۲×۲ با حرفِ
   خوانا و وضعیتِ میکروفن. */
/* نوارِ پایینِ قابِ LIVE: پیش‌تر ۱۲ مربعِ خاکستریِ بی‌معنا بود. حالا سه
   نشانگرِ واقعیِ همان جلسه (حضور، زیرنویس، ضبط).
   NOTE: LIVE_FOOT moved inside Hero3DClassroom (below) so each `text` can be
   resolved via t() — array literals can't call hooks at module scope. */

// S-LAND-VID — ضبطِ واقعی رسید و همان جایگزینی که بالا وعده داده شده بود انجام
// شد: ویدیو **روی** صحنهٔ برداری می‌نشیند و صحنه دست‌نخورده زیرش می‌ماند، پس
// اگر فایل نرسد/کدک پشتیبانی نشود، همان نمای برداریِ قبلی دیده می‌شود (نه قابِ
// سیاه). خواستهٔ مالک: پخشِ خودکار، حلقهٔ دائم، بدونِ هیچ کنترلی و بدونِ دانلود.
//   * `?v=N` نسخه‌بندیِ دستی است: فایلِ public هش نمی‌گیرد و nginx آن را ۳۰ روز
//     کش می‌کند؛ برای تعویضِ ویدیو فقط این عدد جلو می‌رود. **هر بار که محتوای
//     `public/media/live-class.mp4` عوض شد این عدد باید جلو برود، وگرنه بازدیدکنندهٔ
//     قبلی تا ۳۰ روز نسخهٔ کهنه را از کشِ مرورگر می‌بیند.**
//     v=2 → ۱۴۰۵/۰۵/۱۵، جایگزینیِ ضبطِ تازهٔ مالک.
//     v=3 → ۱۴۰۵/۰۵/۱۶، نسخهٔ تازهٔ مالک (۲٬۸۶۳٬۶۸۸ → ۲٬۶۱۴٬۰۸۱ بایت).
//   * هم‌مبدأ (`/media/…`) — نه CDN (قاعدهٔ سختِ ۵) و سازگار با media-src 'self'.
const LIVE_VIDEO_SRC = "/media/live-class.mp4?v=3";

/** ویدیوی قابِ کلاسِ زنده — بی‌صدا، خودپخش، حلقه‌ای، بدونِ هیچ کنترلی. */
const LiveClassVideo: React.FC = () => {
  const ref = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // خودپخشِ بی‌صدا در همهٔ مرورگرهای امروزی مجاز است، اما iOS در حالتِ
    // «کم‌مصرف» و بعضی مرورگرها در تبِ پس‌زمینه آن را رد می‌کنند. هر رد شدن
    // بی‌سروصدا نادیده گرفته و در اولین فرصت (بازگشت به تب یا اولین تعاملِ
    // کاربر با صفحه) دوباره تلاش می‌شود — بدونِ اینکه دکمه‌ای به کاربر نشان دهیم.
    // `play()` در مرورگرهای امروزی Promise می‌دهد ولی در نسخه‌های قدیمی (و در
    // jsdom) undefined؛ بدونِ این گارد یک TypeError کلِ افکت را می‌شکست.
    const kick = (): void => {
      const started = v.play() as Promise<void> | undefined;
      if (started && typeof started.catch === "function") started.catch(() => undefined);
    };
    kick();
    const onVisible = (): void => {
      if (!document.hidden) kick();
    };
    const once = { once: true, passive: true } as const;
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pointerdown", kick, once);
    window.addEventListener("touchstart", kick, once);
    window.addEventListener("keydown", kick, once);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("keydown", kick);
    };
  }, []);

  return (
    <video
      // ری‌اکت `muted` را فقط به‌صورتِ **پراپرتی** ست می‌کند و صفتِ HTML آن را
      // نمی‌نویسد؛ همان صفت است که مرورگر هنگامِ تصمیم‌گیری دربارهٔ خودپخش
      // می‌خواند. پس همین‌جا — سرِ اتصالِ گره و پیش از شروعِ لود — هر دو ست
      // می‌شوند (`defaultMuted` صفت را بازتاب می‌دهد).
      ref={(node) => {
        ref.current = node;
        if (node) {
          node.defaultMuted = true;
          node.muted = true;
        }
      }}
      className="lp-live-video"
      src={LIVE_VIDEO_SRC}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      // بدونِ کنترل، بدونِ منویِ راست‌کلیک، بدونِ تصویر-در-تصویر، بدونِ
      // پخشِ از راه دور و بدونِ تمام‌صفحه: قاب فقط یک تصویرِ متحرک است.
      controls={false}
      controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
      disablePictureInPicture
      disableRemotePlayback
      tabIndex={-1}
      aria-hidden="true"
      onContextMenu={(e) => e.preventDefault()}
      // بعضی نسخه‌های سافاری با playsinline حلقه را رها می‌کنند؛ این تضمینِ دوم است.
      onEnded={(e) => {
        const v = e.currentTarget;
        v.currentTime = 0;
        const again = v.play() as Promise<void> | undefined;
        if (again && typeof again.catch === "function") again.catch(() => undefined);
      }}
    />
  );
};

const Hero3DClassroom = () => {
  const { t } = useTranslation("landing");
  const LIVE_FOOT = [
    { icon: "users", text: t("home.hero3d.classroom.foot.attendance") },
    { icon: "captions", text: t("home.hero3d.classroom.foot.captions") },
    { icon: "video", text: t("home.hero3d.classroom.foot.recording") },
  ] as const;
  const LIVE_TILES = [
    { id: "t1", initial: t("home.hero3d.classroom.tiles.t1.initial"), muted: false },
    { id: "t2", initial: t("home.hero3d.classroom.tiles.t2.initial"), muted: true },
    { id: "t3", initial: t("home.hero3d.classroom.tiles.t3.initial"), muted: true },
    { id: "t4", initial: t("home.hero3d.classroom.tiles.t4.initial"), muted: true },
  ];
  return (
  <div className="hero-card hc-classroom">
    <div className="head">
      <div className="title">{t("home.hero3d.classroom.title")}</div>
      <div className="meta">{t("home.hero3d.classroom.meta")}</div>
    </div>
    <div
      className="mini-video lp-live-scene has-video"
      role="img"
      aria-label={t("home.hero3d.classroom.ariaLabel")}
    >
      <span className="live-pill"><span className="dot"></span>LIVE</span>
      <LiveClassVideo />
      <div className="lp-live-stage" aria-hidden="true">
        {/* اشتراکِ صفحهٔ استاد */}
        <div className="lp-live-share">
          <div className="lp-live-share-bar">
            <i /><i /><i />
          </div>
          <div className="lp-live-slide">
            <div className="lp-live-slide-title" />
            <div className="lp-live-chart">
              {[42, 64, 51, 78, 60, 88].map((h, i) => (
                <span key={i} style={{ height: `${h}%`, animationDelay: `${i * 0.14}s` }} />
              ))}
            </div>
          </div>
          <span className="lp-live-pointer" />
        </div>
        {/* ستونِ تایلِ شرکت‌کننده‌ها — حلقهٔ «در حال صحبت» نوبتی می‌چرخد */}
        <div className="lp-live-tiles">
          {LIVE_TILES.map((tile, i) => (
            <span
              key={tile.id}
              className="lp-live-tile"
              data-muted={tile.muted ? "true" : "false"}
              style={{ animationDelay: `${i * 1.6}s` }}
            >
              {tile.initial}
            </span>
          ))}
        </div>
      </div>
      {/* نوارِ زیرنویسِ زنده (STT) */}
      <div className="lp-live-caption" aria-hidden="true">
        <span>{t("home.hero3d.classroom.caption")}</span>
      </div>
      {/* برچسب به بالا-چپ رفت: پیش‌تر با نوارِ زیرنویس در پایین هم‌جا بود و
          بریده دیده می‌شد (بازخوردِ مالک، عکس ۲). */}
      <span className="label">{t("home.hero3d.classroom.label")}</span>
    </div>
    <div className="lp-live-foot">
      {LIVE_FOOT.map((f) => (
        <span key={f.text} className="lp-live-foot-item">
          <Icon name={f.icon} size={12} />
          {f.text}
        </span>
      ))}
    </div>
  </div>
  );
};

/* بازخوردِ مالک (عکس ۲) — حباب‌ها «نوارِ خاکستریِ خالی» دیده می‌شدند.
   حالا پاسخ کامل است و — مطابقِ قاعدهٔ ۹ حاکمیتِ هوش مصنوعی — منبع و
   درجهٔ اطمینان هم زیرش می‌آید؛ همان چیزی که واقعاً در اپ نشان داده
   می‌شود، نه یک زینتِ تزئینی. */
const Hero3DTutor = () => {
  const { t } = useTranslation("landing");
  return (
  <div className="hero-card hc-tutor">
    <div className="head">
      <div className="title">{t("home.hero3d.tutor.title")}</div>
      <div className="meta">RAG</div>
    </div>
    <div className="tutor-bubble user">
      <div className="av">{t("home.hero3d.tutor.userAvatar")}</div>
      <div>{t("home.hero3d.tutor.userQuestion")}</div>
    </div>
    <div className="tutor-bubble">
      <div className="av">{t("home.hero3d.tutor.answerAvatar")}</div>
      <div>{t("home.hero3d.tutor.answer")}</div>
    </div>
    <div className="lp-tutor-meta">
      <span className="lp-tutor-src">
        <Icon name="video" size={11} />
        {t("home.hero3d.tutor.sourceLabel")}
      </span>
      <span className="lp-tutor-conf">{t("home.hero3d.tutor.confidence")}</span>
    </div>
  </div>
  );
};

/* بازخوردِ مالک (عکس ۲) — نوارها ناهم‌تراز بودند و آبی/قرمز/زردِ تیره با
   هم جنگ داشتند. حالا شبکهٔ سه‌ستونیِ هم‌تراز و فقط پالتِ برند:
   سرمه‌ای (سالم) ← طلایی (توجه) ← گلگون (هشدار). */
// NOTE: CLASS_HEALTH moved inside Hero3DAnalytics (below) so each label can
// be resolved via t() — array literals can't call hooks at module scope.

const Hero3DAnalytics = () => {
  const { t } = useTranslation("landing");
  const CLASS_HEALTH = [
    { label: t("home.hero3d.analytics.metrics.engagement"), value: 82, tone: "" },
    { label: t("home.hero3d.analytics.metrics.comprehension"), value: 64, tone: "warn" },
    { label: t("home.hero3d.analytics.metrics.pace"), value: 94, tone: "alert" },
    { label: t("home.hero3d.analytics.metrics.ambiguity"), value: 38, tone: "warn" },
  ];
  return (
  <div className="hero-card hc-analytics">
    <div className="head">
      <div className="title">{t("home.hero3d.analytics.title")}</div>
      <div className="meta">{t("home.hero3d.analytics.meta")}</div>
    </div>
    {CLASS_HEALTH.map((m) => (
      <div className="bar-row" key={m.label}>
        <div className="bar-label">{m.label}</div>
        <div className="bar-track">
          <div className={`bar-fill ${m.tone}`} style={{ width: `${m.value}%` }} />
        </div>
        <div className="bar-val">{toFa(m.value)}</div>
      </div>
    ))}
    <div className="lp-analytics-note">
      <Icon name="sparkle" size={11} />
      {t("home.hero3d.analytics.note")}
    </div>
  </div>
  );
};

/* بازخوردِ مالک (عکس ۲) — مُهر «VC» و کدِ بی‌معنیِ «#7f3a · OB 3.0» جای
   خود را به یک گواهیِ باورپذیر داد: مُهرِ تأیید، عنوانِ برنامه، و کدِ
   استعلامی هم‌قالبِ رجیستریِ واقعیِ S-CRED. */
const Hero3DCredential = () => {
  const { t } = useTranslation("landing");
  return (
  <div className="hero-card hc-credential">
    <div className="head">
      <div className="title">{t("home.hero3d.credential.title")}</div>
      <div className="meta">{t("home.hero3d.credential.meta")}</div>
    </div>
    <div className="credential-mini">
      <div className="credential-seal float" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor"
          strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="m5 12 5 5L20 7" />
        </svg>
      </div>
      <div className="lp-cred-kicker">{t("home.hero3d.credential.kicker")}</div>
      <div className="lp-cred-title">{t("home.hero3d.credential.programTitle")}</div>
      <div className="lp-cred-code latin">{BRAND.credentialCodePrefix}-1405-04821</div>
      <div className="lp-cred-verify">
        <Icon name="shield" size={11} />
        {t("home.hero3d.credential.verify", { domain: BRAND.domain })}
      </div>
    </div>
  </div>
  );
};

// ====== Phase-16 R9 — Trust strip (§2) ======
//
// Six partner logos rendered as inline SVGs so we don't pay an extra
// HTTP round-trip per logo. Grayscale by default — saturate on hover.
// The placeholders are generic monograms; replace with real partners
// in a future content sprint.
// NOTE: the monograms moved into the catalogue (they are letters of the
// partner's name, so they differ per locale); `name` stays in code because
// the six labels are Latin acronyms in every locale.
const TRUST_PARTNERS = [
  { id: "msa", name: "MSA" },
  { id: "ut", name: "UT" },
  { id: "sharif", name: "Sharif" },
  { id: "khu", name: "KHU" },
  { id: "iut", name: "IUT" },
  { id: "amir", name: "Amirkabir" },
];

const TrustStrip = () => {
  const { t } = useTranslation("landing");
  return (
  <section className="trust-strip" aria-label={t("home.trustStrip.ariaLabel")}>
    <div className="shell">
      <div className="trust-strip-eyebrow">
        <span className="dot"></span>
        <span>{t("home.trustStrip.eyebrow")}</span>
      </div>
      <ul className="trust-strip-row">
        {TRUST_PARTNERS.map((p) => (
          <li key={p.id} className="trust-strip-item" title={p.name}>
            <span className="trust-strip-glyph" aria-hidden="true">
              {t(`home.trustStrip.partners.${p.id}.glyph`)}
            </span>
            <span className="trust-strip-name">{p.name}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

// ====== Phase-16 R9 — Animated stats band (§3) ======
//
// Four numbers count up when the band scrolls into view. We honour
// `prefers-reduced-motion: reduce` by skipping the animation and
// rendering the final value immediately.
// NOTE: STATS_BAND moved inside StatsBand (below) so each label can be
// resolved via t() — array literals can't call hooks at module scope.

const StatsBand = () => {
  const { t } = useTranslation("landing");
  const STATS_BAND = [
    { value: 8, suffix: "", label: t("home.statsBand.faculties") },
    { value: 248, suffix: "", label: t("home.statsBand.programs") },
    { value: 94, suffix: "", label: t("home.statsBand.instructors") },
    { value: 8400, suffix: "+", label: t("home.statsBand.students") },
  ];
  const ref = React.useRef(null);
  const [displayed, setDisplayed] = React.useState(() => STATS_BAND.map(() => 0));
  const [hasRun, setHasRun] = React.useState(false);

  React.useEffect(() => {
    if (hasRun) return;
    const el = ref.current;
    if (!el) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplayed(STATS_BAND.map((s) => s.value));
      setHasRun(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible || hasRun) return;
        setHasRun(true);
        const start = performance.now();
        const DURATION = 1400; // ms
        const tick = (now) => {
          const frac = Math.min(1, (now - start) / DURATION);
          // ease-out cubic for a friendly count-up
          const eased = 1 - Math.pow(1 - frac, 3);
          setDisplayed(STATS_BAND.map((s) => Math.round(s.value * eased)));
          if (frac < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasRun]);

  return (
    <section className="stats-band" ref={ref} aria-label={t("home.statsBand.ariaLabel")}>
      <div className="shell">
        <div className="stats-band-grid">
          {STATS_BAND.map((s, i) => (
            <div className="stats-band-cell" key={s.label}>
              <div className="stats-band-value">
                {toFa(displayed[i])}
                {s.suffix}
              </div>
              <div className="stats-band-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ====== Phase-16 R10 §4 — Faculty showcase ======
//
// 6 instructor cards in a responsive grid. Each card reuses the
// existing 3D treatment (paper shadow + accent border on hover). The
// data is hardcoded here for now — TODO(phase-17) pull from
// /v1/users?role=instructor.
// Only the roster ids live in code; every visible field comes from the
// catalogue (a name written in Persian script is not readable copy under
// `ar`, so the Arabic transliteration is a translation, not a duplicate).
const FACULTY_IDS = ["azimi", "kiani", "razavi", "mousavi", "taheri", "farhadi"] as const;

const FacultyShowcase = ({ go }) => {
  const { t } = useTranslation("landing");
  return (
  <section className="section faculty-showcase" aria-label={t("home.facultyShowcase.ariaLabel")}>
    <div className="shell">
      <div className="section-head reveal">
        <div className="text">
          <span className="eyebrow">{t("home.facultyShowcase.eyebrow")}</span>
          <h2 className="h-1">{t("home.facultyShowcase.titlePrefix")} <span style={{ color: "var(--accent)" }}>{t("home.facultyShowcase.titleHighlight")}</span></h2>
        </div>
        <p className="lead">{t("home.facultyShowcase.lead")}</p>
      </div>
      <div className="faculty-grid">
        {FACULTY_IDS.map((id) => (
          <article key={id} className="faculty-card reveal">
            <div className="faculty-avatar" aria-hidden="true">{t(`home.facultyShowcase.people.${id}.initials`)}</div>
            <h3 className="faculty-name">{t(`home.facultyShowcase.people.${id}.name`)}</h3>
            <span className="faculty-affiliation">{t(`home.facultyShowcase.people.${id}.affiliation`)}</span>
            <p className="faculty-bio">{t(`home.facultyShowcase.people.${id}.bio`)}</p>
          </article>
        ))}
      </div>
      <div className="faculty-cta">
        <Button variant="outline" onClick={() => go("programs")}>
          {t("home.facultyShowcase.viewAllButton")}
          <Icon name="arrow" size={14} />
        </Button>
      </div>
    </div>
  </section>
  );
};

// ====== Phase-16 R10 §5 — Catalog teaser ======
//
// 6 featured courses in a responsive grid. On `<sm` they switch to a
// horizontal scroll-snap carousel so the user sees one course at a
// time without crammed half-cards. Container query inside the grid
// item makes the inner layout adapt to the slot, not just the
// viewport (so a course card in a sidebar elsewhere stacks
// vertically instead of overflowing).
// NOTE: CATALOG_TEASER moved inside CatalogTeaser (below) so each entry's
// title/by/desc can be resolved via t() — array literals can't call hooks
// at module scope.

const CatalogTeaser = ({ go }) => {
  const { t } = useTranslation("landing");
  const CATALOG_TEASER = [
    { code: "CS-410", title: t("home.catalogTeaser.items.mlFoundations.title"), by: t("home.catalogTeaser.items.mlFoundations.by"), desc: t("home.catalogTeaser.items.mlFoundations.desc"), weeks: toFa(12), glyph: "ML" },
    { code: "CS-580", title: t("home.catalogTeaser.items.scalableSystems.title"), by: t("home.catalogTeaser.items.scalableSystems.by"), desc: t("home.catalogTeaser.items.scalableSystems.desc"), weeks: toFa(10), glyph: "SYS" },
    { code: "PM-310", title: t("home.catalogTeaser.items.dataProductDesign.title"), by: t("home.catalogTeaser.items.dataProductDesign.by"), desc: t("home.catalogTeaser.items.dataProductDesign.desc"), weeks: toFa(8), glyph: "PM" },
    { code: "CS-620", title: t("home.catalogTeaser.items.advancedNlp.title"), by: t("home.catalogTeaser.items.advancedNlp.by"), desc: t("home.catalogTeaser.items.advancedNlp.desc"), weeks: toFa(14), glyph: "NLP" },
    { code: "PHIL-220", title: t("home.catalogTeaser.items.aiEthics.title"), by: t("home.catalogTeaser.items.aiEthics.by"), desc: t("home.catalogTeaser.items.aiEthics.desc"), weeks: toFa(6), glyph: "AI⁺" },
    { code: "STAT-440", title: t("home.catalogTeaser.items.appliedBayesianStats.title"), by: t("home.catalogTeaser.items.appliedBayesianStats.by"), desc: t("home.catalogTeaser.items.appliedBayesianStats.desc"), weeks: toFa(10), glyph: "Σ" },
  ];

  return (
    <section className="section catalog-teaser" aria-label={t("home.catalogTeaser.ariaLabel")}>
      <div className="shell">
        <div className="section-head reveal">
          <div className="text">
            <span className="eyebrow">{t("home.catalogTeaser.eyebrow")}</span>
            <h2 className="h-1">{t("home.catalogTeaser.titlePrefix")} <span style={{ color: "var(--navy)" }}>{t("home.catalogTeaser.titleHighlight")}</span></h2>
          </div>
          <p className="lead">{t("home.catalogTeaser.lead")}</p>
        </div>
        <div className="catalog-teaser-rail">
          {CATALOG_TEASER.map((c) => (
            <article key={c.code} className="catalog-teaser-card reveal">
              <div className={"catalog-teaser-glyph"}>{c.glyph}</div>
              <div className="catalog-teaser-body">
                <div className="catalog-teaser-code">{c.code}</div>
                <h3 className="catalog-teaser-title">{c.title}</h3>
                <p className="catalog-teaser-by">{c.by}</p>
                <p className="catalog-teaser-desc">{c.desc}</p>
                <div className="catalog-teaser-meta">
                  <span>{c.weeks} {t("home.catalogTeaser.weeksUnit")}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="catalog-teaser-cta">
          <Button variant="outline" onClick={() => go("catalog")}>
            {t("home.catalogTeaser.viewAllButton")}
            <Icon name="arrow" size={14} />
          </Button>
        </div>
      </div>
    </section>
  );
};

// ====== Phase-16 R10 §6 — Testimonials + dual CTA ======
// Same shape as FACULTY_IDS above: ids in code, every visible field keyed.
const ALUMNI_IDS = ["nasrinRazavi", "aliJavadi", "mahsaKiani"] as const;

const Testimonials = ({ go }) => {
  const { t } = useTranslation("landing");
  return (
  <section className="section testimonials" aria-label={t("home.alumniVoices.ariaLabel")}>
    <div className="shell">
      <div className="section-head reveal">
        <div className="text">
          <span className="eyebrow">{t("home.alumniVoices.eyebrow")}</span>
          <h2 className="h-1">{t("home.alumniVoices.titlePrefix")} <span style={{ color: "var(--sage)" }}>{t("home.alumniVoices.titleHighlight")}</span></h2>
        </div>
        <p className="lead">{t("home.alumniVoices.lead")}</p>
      </div>
      <div className="testimonials-grid">
        {ALUMNI_IDS.map((id) => (
          <article key={id} className="testimonial-card reveal">
            <svg
              className="testimonial-mark"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              aria-hidden="true"
              fill="currentColor"
            >
              <path d="M9 8c-3 1-5 4-5 8v8h8v-9H7c0-2 1-4 3-5l-1-2zm14 0c-3 1-5 4-5 8v8h8v-9h-5c0-2 1-4 3-5l-1-2z" />
            </svg>
            <p className="testimonial-quote">«{t(`home.alumniVoices.people.${id}.quote`)}»</p>
            <footer className="testimonial-footer">
              <span className="testimonial-avatar" aria-hidden="true">{t(`home.alumniVoices.people.${id}.initials`)}</span>
              <span>
                <strong>{t(`home.alumniVoices.people.${id}.name`)}</strong>
                <br />
                <span>{t(`home.alumniVoices.people.${id}.role`)}</span>
              </span>
            </footer>
          </article>
        ))}
      </div>
      <div className="testimonials-cta-row">
        <Button variant="primary" size="lg" onClick={() => go("apply")}
        >
          {t("home.alumniVoices.ctaStart")}
          <Icon name="arrow" size={16} />
        </Button>
        <Button variant="outline" size="lg" onClick={() => go("programs")}
        >
          {t("home.alumniVoices.ctaPrograms")}
          <Icon name="grad" size={16} />
        </Button>
      </div>
    </div>
  </section>
  );
};

// ====== Course catalog data ======
export default HomePage;
